import React, { useContext, useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { TableContext } from './context/TableContext';
import { EditModeContext } from './context/EditModeContext';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import TableFooter from './TableFooter';
import clsx from 'clsx';

const BUFFER_SIZE = 50; // Number of rows to render above/below viewport
const MIN_RENDERED_ROWS = 100; // Minimum number of rows to render initially
const FOOTER_HEIGHT = 48;

export const ROW_HEIGHT = {
  compact: 32,
  standard: 64,
  comfortable: 128
};

const BaseTable = ({
  data = [],
  columns,
  selectedItems = new Set(),
  onSelectionChange,
  onRowClick,
  showThumbnails = true,
  renderCustomRow,
}) => {
  const [isShiftPressed, setIsShiftPressed] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const editModeContext = useContext(EditModeContext);
  const isEditMode = editModeContext?.isEditMode || false;
  const { 
    filterRows, 
    sortRows, 
    applyFilters,
    getVisibleColumns,
    loading,
    error,
    preferences: { density },
    columnWidths,
    isColumnPinned
  } = useContext(TableContext);

  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const lastScrollTop = useRef(0);
  const scrollRAF = useRef(null);
  const isInitialized = useRef(false);
  
  const [visibleRange, setVisibleRange] = useState(() => ({
    start: 0,
    end: Math.min(data.length, MIN_RENDERED_ROWS)
  }));
  const rowHeight = ROW_HEIGHT[density] || ROW_HEIGHT.standard;

  const visibleColumns = useMemo(() => 
    getVisibleColumns(columns), 
    [getVisibleColumns, columns]
  );

  const lastSelectedId = useRef(null);
  
  const normalizedSelectedItems = useMemo(() => {
    const items = selectedItems instanceof Set ? selectedItems : new Set(selectedItems);
    return new Set([...items].map(id => String(id)));
  }, [selectedItems]);

  const filteredData = useMemo(() => 
    filterRows(data),
    [filterRows, data]
  );

  const processedData = useMemo(() =>
    sortRows(applyFilters(filteredData)),
    [sortRows, applyFilters, filteredData]
  );

  const handleCheckboxChange = useCallback((itemId, isShiftKey) => {
    onSelectionChange(prevSelection => {
      const stringId = String(itemId);
      const newSelection = new Set(prevSelection);

      if (isShiftKey && lastSelectedId.current) {
        // Find indices of last selected and current items
        const lastIndex = processedData.findIndex(item => String(item.id) === lastSelectedId.current);
        const currentIndex = processedData.findIndex(item => String(item.id) === stringId);
        
        if (lastIndex !== -1 && currentIndex !== -1) {
          // Select all items between last selected and current
          const start = Math.min(lastIndex, currentIndex);
          const end = Math.max(lastIndex, currentIndex);
          
          for (let i = start; i <= end; i++) {
            newSelection.add(String(processedData[i].id));
          }
        }
      } else {
        // Normal toggle behavior
        if (newSelection.has(stringId)) {
          newSelection.delete(stringId);
        } else {
          newSelection.add(stringId);
        }
        lastSelectedId.current = stringId;
      }
      
      return new Set([...newSelection]);
    });
  }, [onSelectionChange, processedData]);

  const handleHeaderCheckboxChange = useCallback(() => {
    onSelectionChange(prevSelection => {
      if (prevSelection.size === processedData.length) {
        return new Set();
      }
      return new Set(processedData.map(item => String(item.id)));
    });
  }, [processedData, onSelectionChange]);

  const calculateVisibleRange = useCallback(() => {
    if (!containerRef.current || !processedData.length) return;

    const { scrollTop, clientHeight } = containerRef.current;
    const adjustedClientHeight = Math.max(clientHeight - FOOTER_HEIGHT, 1);
    
    // Calculate viewport boundaries
    const viewportStart = Math.floor(scrollTop / rowHeight);
    const rowsInView = Math.ceil(adjustedClientHeight / rowHeight);
    const viewportEnd = viewportStart + rowsInView;
    
    // Calculate range with buffer
    const start = Math.max(0, viewportStart - BUFFER_SIZE);
    const end = Math.min(
      processedData.length,
      Math.max(viewportEnd + BUFFER_SIZE * 2, MIN_RENDERED_ROWS)
    );
    
    // Only update if range has changed
    setVisibleRange(prev => {
      if (prev.start === start && prev.end === end) return prev;
      return { start, end };
    });
  }, [rowHeight, processedData.length]);

  const syncScroll = useCallback((scrollLeft) => {
    if (headerRef.current) {
      headerRef.current.scrollLeft = scrollLeft;
    }
    if (footerRef.current) {
      footerRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  const handleScroll = useCallback((e) => {
    if (e.target !== containerRef.current) return;

    // Sync horizontal scroll
    syncScroll(e.target.scrollLeft);

    // Update visible range on vertical scroll using RAF
    if (e.target.scrollTop !== lastScrollTop.current) {
      if (scrollRAF.current) {
        cancelAnimationFrame(scrollRAF.current);
      }
      scrollRAF.current = requestAnimationFrame(() => {
        calculateVisibleRange();
        lastScrollTop.current = e.target.scrollTop;
      });
    }
  }, [calculateVisibleRange, syncScroll]);

  // Setup event listeners and observers
  useEffect(() => {
    const container = containerRef.current;
    if (!container || loading) return;

    // Reset scroll position
    container.scrollTop = 0;
    lastScrollTop.current = 0;

    // Add scroll listener
    container.addEventListener('scroll', handleScroll, { passive: true });
    
    // Setup resize observer
    const resizeObserver = new ResizeObserver(() => {
      if (scrollRAF.current) {
        cancelAnimationFrame(scrollRAF.current);
      }
      scrollRAF.current = requestAnimationFrame(calculateVisibleRange);
    });
    resizeObserver.observe(container);

    // Initial calculation
    calculateVisibleRange();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      if (scrollRAF.current) {
        cancelAnimationFrame(scrollRAF.current);
      }
    };
  }, [handleScroll, calculateVisibleRange, loading]);

  // Handle data updates
  useEffect(() => {
    if (!loading && processedData.length > 0) {
      // Reset visible range with new data
      setVisibleRange({
        start: 0,
        end: Math.min(processedData.length, MIN_RENDERED_ROWS)
      });

      // Schedule range calculation after state update
      setTimeout(() => {
        if (containerRef.current) {
          calculateVisibleRange();
        }
      }, 0);
    }
  }, [loading, processedData.length, calculateVisibleRange]);

  // Handle density changes
  useEffect(() => {
    if (!loading && containerRef.current) {
      calculateVisibleRange();
    }
  }, [density, calculateVisibleRange, loading]);

  const totalHeight = processedData.length * rowHeight;
  const visibleData = processedData.slice(visibleRange.start, visibleRange.end);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-red-600 bg-red-50 px-4 py-3 rounded-lg">
          Failed to load data: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className={clsx(
        "bg-white shadow-lg h-full rounded-b-xl -mt-px",
        isEditMode && "border border-gray-300"
      )}>
        <div 
          ref={containerRef}
          className="h-full overflow-auto always-show-scrollbar"
          style={{ 
            willChange: 'transform',
            transform: 'translate3d(0, 0, 0)',
            WebkitOverflowScrolling: 'touch',
            overflowY: 'scroll',
            paddingRight: '1px' // Force scrollbar to appear
          }}
        >
          <div className="inline-block min-w-full" style={{ paddingBottom: '1px', minWidth: 'fit-content' }}>
          <table 
            className={clsx(
              "w-full border-collapse",
              isEditMode && "border-collapse"
            )}
            style={{
              tableLayout: 'fixed',
              width: 'max-content', // Prevent table from expanding beyond column widths
              minWidth: '100%'
            }}>
              <colgroup>
                {onSelectionChange && (
                  <col 
                    style={{ 
                      width: '50px',
                      minWidth: '50px',
                      maxWidth: '50px'
                    }} 
                  />
                )}
                {visibleColumns.map(col => (
                  <col 
                    key={col.field} 
                    style={{ 
                      width: `${columnWidths[col.field] || 150}px`,
                      minWidth: `${columnWidths[col.field] || 150}px`,
                      maxWidth: `${columnWidths[col.field] || 150}px`
                    }} 
                  />
                ))}
              </colgroup>
              <TableHeader
                ref={headerRef}
                columns={visibleColumns}
                selectedItems={onSelectionChange ? normalizedSelectedItems : new Set()}
                totalItems={processedData.length}
                onHeaderCheckboxChange={onSelectionChange ? handleHeaderCheckboxChange : undefined}
                tableRef={containerRef}
              />
              <tbody className={clsx(
                "bg-white",
                isEditMode && "border-t border-gray-300"
              )}>
                <tr
                  style={{ height: visibleRange.start * rowHeight }}
                  aria-hidden="true"
                  data-spacer="top"
                >
                  <td colSpan={onSelectionChange ? visibleColumns.length + 1 : visibleColumns.length} />
                </tr>
                {visibleData.map((item) => (
                  renderCustomRow ? (
                    renderCustomRow({
                      key: item.id,
                      item,
                      visibleColumns,
                      selectedItems: onSelectionChange ? normalizedSelectedItems : new Set(),
                      onRowClick,
                      onCheckboxChange: onSelectionChange ? handleCheckboxChange : undefined
                    })
                  ) : (
                    <TableRow
                      key={item.id}
                      item={item}
                      visibleColumns={visibleColumns}
                      selectedItems={onSelectionChange ? normalizedSelectedItems : new Set()}
                      onRowClick={onRowClick}
                      onCheckboxChange={onSelectionChange ? handleCheckboxChange : undefined}
                    />
                  )
                ))}
                <tr style={{ height: Math.max(0, ((processedData.length - visibleRange.end) * rowHeight)) }} aria-hidden="true">
                  <td colSpan={onSelectionChange ? visibleColumns.length + 1 : visibleColumns.length} />
                </tr>
              </tbody>
              <tfoot className={clsx(
                "sticky bottom-0 left-0 right-0 bg-indigo-50 z-[20]",
                isEditMode ? "border-t border-gray-300" : "border-t border-gray-200"
              )}>
                <tr className="h-12">
                  {onSelectionChange && (
                    <td className={clsx(
                      "sticky left-0 px-3 text-xs text-gray-500 bg-indigo-50 z-[21]",
                      isEditMode && "border-r border-gray-300"
                    )}>
                      {processedData.length} Items
                    </td>
                  )}
                  <TableFooter 
                    data={processedData}
                    visibleColumns={visibleColumns}
                    columnWidths={columnWidths}
                  />
                </tr>
              </tfoot>
            </table>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseTable;
